import {
  CorporateActionProcessingRequestRow,
  CorporateActionsProcessingBatchRequest,
  ProcessingGridRow,
} from '../models/corporate-action-processing.model'
import { toIsoExDate } from '../validators/corporate-actions-processing-validator'

/**
 * Build the batch POST body from grid rows (PVAL-25952).
 *
 * ALL type coercion happens here, at request-build time — never at cell-edit
 * time, where it would fight the inline editors. ag-grid inline editors yield
 * strings, so a naive `rows.push(node.data)` would send
 * `accountId: "123"` and `destShareExchRate2: ""`. The backend DTO
 * (`ProcessCashStockAcquisitionRow`) types these as Kotlin `Int`, `LocalDate`,
 * `String?` and non-null `Double`, so Jackson rejects the WHOLE request body on
 * a single mismatch: the response then carries no per-row `results` at all and
 * every row is lost with no actionable message. That is a batch-level failure
 * mode unlike everything else on this ticket, which is why it is isolated in a
 * pure, unit-tested function.
 */

/** Server-side default for `destShareExchRate2` (`Double = 1.0`). */
export const DEST_SHARE_EXCH_RATE_2_DEFAULT = 1.0
/** Server-side default for `destCusipMarketPrice2` (`Double = 0.0`). */
export const DEST_CUSIP_MARKET_PRICE_2_DEFAULT = 0.0
/**
 * Server-side default for `forceRealizeAll` (`Boolean = false`).
 *
 * The flag IS an editable checkbox column, so this is the fallback for a row that
 * does not carry the field — a fixture, or a row object built before the column
 * existed — rather than a hardcoded wire value. Sending it explicitly keeps every
 * request body self-describing instead of leaning on the Kotlin default.
 */
export const FORCE_REALIZE_ALL_DEFAULT = false

function toNumber(value: string | undefined, fallback: number): number {
  const trimmed = (value ?? '').trim()
  if (trimmed.length === 0) {
    return fallback
  }
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * Coerce a single grid row to its wire form.
 *
 * Dest CUSIP 2 governs ALL THREE second-destination fields together, because the
 * backend gates them together: `CorporateActionsProcessingCommandService` builds
 * its numerics map inside `if (row.destCusip2 != null) { ... }`, and
 * `validateSecondDestinationNumbers` mirrors that by early-returning when the
 * cell is blank. With no second destination, `destCusip2` is OMITTED (not sent
 * as `''`) and the two numerics are sent as the backend's DECLARED DEFAULTS —
 * never coerced from the row.
 *
 * Coercing them from the row here was a real defect: a user who typed a Dest
 * CUSIP 2 plus `destShareExchRate2 = -5` and then cleared the CUSIP cell (or
 * pasted a block with rate-2 populated and CUSIP-2 empty) left a stale value
 * that the client no longer validates and — by the very backend gate this
 * mirrors — the server does not validate either, so `-5` reached perseus
 * unchecked.
 *
 * `exDate` goes through `toIsoExDate`, not `normalizeExDate`: the validator now
 * accepts `MM/dd/yyyy` too, and this is the one place that converts it back to
 * the `yyyy-MM-dd` Jackson needs. The display form on the wire fails the WHOLE
 * request body (PVAL-26303 #1).
 */
export function toProcessingRequestRow(
  row: ProcessingGridRow,
  commandType: string,
): CorporateActionProcessingRequestRow {
  const destCusip2 = row.destCusip2?.trim() ?? ''
  const hasSecondDestination = destCusip2.length > 0

  const requestRow: CorporateActionProcessingRequestRow = {
    rowId: row.rowId,
    commandType,
    accountId: toNumber(row.accountId, 0),
    exDate: toIsoExDate(row.exDate),
    targetCusip: row.targetCusip.trim(),
    cashPerShare: toNumber(row.cashPerShare, 0),
    cashReceived: toNumber(row.cashReceived, 0),
    destCusip: row.destCusip.trim(),
    destShareExchRate: toNumber(row.destShareExchRate, 0),
    destCusipMarketPrice: toNumber(row.destCusipMarketPrice, 0),
    destShareExchRate2: hasSecondDestination
      ? toNumber(row.destShareExchRate2, DEST_SHARE_EXCH_RATE_2_DEFAULT)
      : DEST_SHARE_EXCH_RATE_2_DEFAULT,
    destCusipMarketPrice2: hasSecondDestination
      ? toNumber(row.destCusipMarketPrice2, DEST_CUSIP_MARKET_PRICE_2_DEFAULT)
      : DEST_CUSIP_MARKET_PRICE_2_DEFAULT,
    notes: row.notes.trim(),
    // Checkbox flags are real booleans on the row, but a row built before the
    // flag columns existed (or a spec fixture) may not carry them at all — and
    // `undefined` would serialize as an absent key, leaning on the Kotlin
    // default. Send the declared default explicitly instead.
    ignoreCostBasisAdjustments: row.ignoreCostBasisAdjustments ?? false,
    forceRealizeAll: row.forceRealizeAll ?? FORCE_REALIZE_ALL_DEFAULT,
  }

  if (hasSecondDestination) {
    requestRow.destCusip2 = destCusip2
  }

  return requestRow
}

export function buildProcessingBatchRequest(
  rows: ProcessingGridRow[],
  commandType: string,
): CorporateActionsProcessingBatchRequest {
  return {
    rows: rows.map(row => toProcessingRequestRow(row, commandType)),
  }
}
